import React from "react";
import { openDispatchingModal, setSetting } from "actions";
import { ModalTypes } from "constants/navigation";
import { useDispatch, useSelector } from "react-redux";
import { getSetting } from "selectors";
import { Settings } from "constants/settings";
import {
  FilterSetting,
  FilterSettings,
  FilterState,
} from "reducers/settings/types";
import { faSliders } from "@fortawesome/pro-solid-svg-icons";
import { LinkButton } from "components/Form";
import { Chip } from "components/Chip";
import { Row } from "components/Layout";
import { SlimButton } from "components/Button";
import { FaIcon } from "components/AgGridButtonIcon/agGridButtonIcon";
import styled from "styled-components/macro";

interface FilterButtonProps {
  filterIds: string[];
}

// Apply custom display styles/logic to the slim button content
const SlimFilterButton = styled(SlimButton)(
  ({ theme }) =>
    `
    & .text { font-weight: 400; }
    & .count { font-weight: 300; }
    @media (max-width: ${theme.breakpoints[0]}px) {
      & .text { display: none; }
    }
  `,
);

export function FilterButton(props: FilterButtonProps): React.JSX.Element {
  const { filterIds = [] } = props;

  const dispatch = useDispatch();

  const filterState: FilterState =
    useSelector(getSetting(Settings.filter)) || {};
  const { filterSettings = [] }: FilterState = filterState;

  function onClickClear(): void {
    const newFilterSettings: FilterSettings = filterSettings.filter(
      (filterSetting: FilterSetting) => !filterIds.includes(filterSetting.id),
    );
    const newFilterState: FilterState = {
      ...filterState,
      filterSettings: newFilterSettings,
    };
    dispatch(setSetting(Settings.filter, newFilterState));
  }

  if (!filterIds) {
    return null;
  }

  // count the number of configured filters that match the applicable filters for this view
  const activeFilterCount: number = filterSettings.filter(
    (filterSetting: FilterSetting) => filterIds.includes(filterSetting.id),
  ).length;

  function onClickFilter() {
    dispatch(
      openDispatchingModal(ModalTypes.Filter, window.location.hash, {
        filterIds,
      }),
    );
  }

  return (
    <>
      <SlimFilterButton
        backgroundColor="controlBackground"
        color="black"
        onClick={onClickFilter}
        borderColor="titleLight"
        borderRadius={4}
        borderSize={1}
        type="button"
      >
        <Row baseline overflow="hidden" gridGap={1}>
          <span className="text">Filter</span>
          <span className="graphic">
            <FaIcon icon={faSliders} color="titleMedium" />
          </span>
          {activeFilterCount > 0 && (
            <span className="count">
              <Chip
                borderRadius={3}
                backgroundColor="blueHighlight"
                borderColor="separatorBlue"
                color="black"
              >
                <span>{activeFilterCount}</span>
              </Chip>
            </span>
          )}
        </Row>
      </SlimFilterButton>
      {activeFilterCount > 0 && (
        <span className="clear">
          <LinkButton onClick={onClickClear}>clear</LinkButton>
        </span>
      )}
    </>
  );
}
